import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Package,
  FolderOpen,
  BarChart3,
  TrendingUp,
  Users,
  ShoppingCart,
  AlertTriangle,
  AlertCircle,
  Eye,
  Edit,
  Plus,
  DollarSign,
  Award,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Receipt,
  FileText,
  RefreshCw,
  Calendar,
  Building2,
  Download
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useAuditorStore } from '../stores/auditorStore'
import { apiClient } from '../services/api'
import { DashboardStats, Product, InvoiceStats, Invoice } from '../types/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { LoadingOverlay } from '../components/LoadingSpinner'
import { useAsyncOperation } from '../hooks/useAsyncOperation'
import { formatCurrency } from '../lib/utils'
import { useNavigate } from 'react-router-dom'

export const Dashboard = () => {
  const { user } = useAuthStore()
  const { isAuditor, currentBusiness, accessibleBusinesses } = useAuditorStore()
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null)
  const [invoiceStats, setInvoiceStats] = useState<InvoiceStats | null>(null)
  const [overdueInvoices, setOverdueInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const loadDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }

      // Fetch dashboard stats and invoice data in parallel
      const [dashboardResponse, invoiceStatsResponse, overdueInvoicesResponse] = await Promise.all([
        apiClient.getDashboardStats(),
        apiClient.getInvoiceStats().catch(() => null),
        apiClient.getOverdueInvoices(5).catch(() => ({ invoices: [], count: 0 }))
      ])

      console.log('Dashboard data:', dashboardResponse)
      setDashboardData(dashboardResponse)

      if (invoiceStatsResponse) {
        setInvoiceStats(invoiceStatsResponse)
      }

      if (overdueInvoicesResponse?.invoices) {
        setOverdueInvoices(overdueInvoicesResponse.invoices)
      }

      setLastUpdated(new Date())
      setError(null)
    } catch (err) {
      console.error('Dashboard loading error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    // For auditors, only load data if a business is selected
    if (isAuditor && !currentBusiness) {
      setLoading(false)
      return
    }

    loadDashboardData()
  }, [isAuditor, currentBusiness?.merchant_id]) // Reload when business changes

  const handleRefresh = () => {
    loadDashboardData(true)
  }

  // Calculate payment method breakdown
  const getPaymentBreakdown = () => {
    if (!dashboardData?.recent_sales) return []

    const breakdown: { [key: string]: { count: number; amount: number } } = {}

    dashboardData.recent_sales.forEach((sale) => {
      const method = sale.payment_method || 'cash'
      if (!breakdown[method]) {
        breakdown[method] = { count: 0, amount: 0 }
      }
      breakdown[method].count++
      breakdown[method].amount += parseFloat(sale.total_amount)
    })

    return Object.entries(breakdown).map(([method, data]) => ({
      method: method.toUpperCase(),
      count: data.count,
      amount: data.amount
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  // Auditor Dashboard View
  if (isAuditor) {
    const auditorQuickActions = [
      {
        icon: FileText,
        label: 'GST Reports',
        description: 'View GSTR-1 and GSTR-3B reports',
        path: '/reports',
        color: 'blue',
      },
      {
        icon: Download,
        label: 'Download Reports',
        description: 'Export reports in PDF/Excel',
        path: '/reports',
        color: 'green',
      },
      {
        icon: TrendingUp,
        label: 'Sales Summary',
        description: 'View sales analytics and trends',
        path: '/invoices',
        color: 'purple',
      },
      {
        icon: Receipt,
        label: 'Invoice List',
        description: 'Browse all invoices with GST details',
        path: '/invoices',
        color: 'orange',
      },
    ]

    return (
      <div className="space-y-6">
        {/* Auditor Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Auditor Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Access GST reports and financial data for your clients
          </p>
        </div>

        {/* Current Business Info */}
        {currentBusiness ? (
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {currentBusiness.name}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">GST Number</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100 font-mono text-sm">
                        {currentBusiness.gst_number}
                      </p>
                    </div>
                    {currentBusiness.city && currentBusiness.state && (
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Location</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {currentBusiness.city}, {currentBusiness.state}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Access Type</p>
                      <Badge variant="success" className="mt-1">Read-Only Access</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-dashed border-gray-300 dark:border-gray-700">
            <CardContent className="py-12">
              <div className="text-center">
                <Building2 className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Select a Business
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  You have access to {accessibleBusinesses.length} business{accessibleBusinesses.length !== 1 ? 'es' : ''}.
                  Use the business switcher in the header above to select a business.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        {currentBusiness && (
          <>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {auditorQuickActions.map((action) => {
                  const Icon = action.icon
                  const colorClasses = {
                    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
                    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
                    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
                    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
                  }[action.color]

                  return (
                    <Link
                      key={action.path}
                      to={action.path}
                      className="block"
                    >
                      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                        <CardContent className="p-6">
                          <div className={`w-12 h-12 rounded-lg ${colorClasses} flex items-center justify-center mb-4`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{action.label}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  )
                })}
              </div>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Your Access
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <li className="flex items-center gap-2">
                      <span className="text-green-600 dark:text-green-400">✓</span>
                      View GST Reports (GSTR-1, GSTR-3B)
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600 dark:text-green-400">✓</span>
                      Access Sales and Purchase Summaries
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600 dark:text-green-400">✓</span>
                      Download Reports in PDF/Excel
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600 dark:text-green-400">✓</span>
                      View Invoice Lists with GST Details
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Accessible Businesses ({accessibleBusinesses.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {accessibleBusinesses.map((business) => (
                      <div
                        key={business.id}
                        className={`text-sm p-3 rounded-lg ${
                          business.id === currentBusiness?.id
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 font-medium border border-blue-200 dark:border-blue-800'
                            : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <div className="font-medium">{business.name}</div>
                        <div className="text-xs opacity-75">{business.gst_number}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Setup Own Business CTA */}
            <Card className="border-2 border-dashed border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10">
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center">
                      <Building2 className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      Setup Your Own Business
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Register your CA firm or practice details to manage your own business alongside auditing clients.
                    </p>
                    <Button
                      onClick={() => navigate('/onboarding')}
                      variant="outline"
                      className="border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/20"
                    >
                      Setup Business
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    )
  }

  // Merchant Dashboard View
  return (
    <div className="space-y-8">
      {/* Welcome Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Welcome back, {user?.full_name}!
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-gray-600 dark:text-gray-400">
              Here's what's happening with {user?.business_name || 'your business'} today.
            </p>
            {dashboardData?.period && (
              <div className="flex items-center gap-1 text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-3 py-1 rounded-full">
                <Calendar className="h-3 w-3" />
                <span>
                  {new Date(dashboardData.period.from).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  {dashboardData.period.from !== dashboardData.period.to &&
                    ` - ${new Date(dashboardData.period.to).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`
                  }
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          {lastUpdated && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      {dashboardData && dashboardData.stats && (
        <>
          {/* Primary Stats Row - Sales Focused */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-green-900 dark:text-green-100">Today's Revenue</CardTitle>
                <DollarSign className="h-5 w-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-700 dark:text-green-300">
                  {formatCurrency(dashboardData?.stats?.sales?.total_revenue || 0)}
                </div>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {dashboardData?.stats?.sales?.total_sales || 0} transactions
                </p>
                <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800">
                  <p className="text-xs text-green-600 dark:text-green-400">
                    Avg: {formatCurrency(dashboardData?.stats?.sales?.average_sale || 0)} per sale
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
                <ShoppingCart className="h-5 w-5 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                  {dashboardData?.stats?.sales?.total_sales || 0}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Transactions completed
                </p>
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Tax collected: {formatCurrency(dashboardData?.stats?.sales?.total_tax || 0)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Customers</CardTitle>
                <Users className="h-5 w-5 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-700 dark:text-purple-300">
                  {dashboardData?.stats?.customers?.total_customers || 0}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Active customers
                </p>
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <Button variant="ghost" size="sm" className="h-6 text-xs -ml-2" asChild>
                    <Link to="/customers">
                      View all <ArrowUpRight className="h-3 w-3 ml-1" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Products</CardTitle>
                <Package className="h-5 w-5 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-700 dark:text-orange-300">
                  {dashboardData?.stats?.products?.total_products || 0}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {dashboardData?.stats?.products?.active_products || 0} active
                </p>
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Value: {formatCurrency(dashboardData?.stats?.products?.total_inventory_value || 0)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Invoice Stats Row */}
          {invoiceStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-blue-900 dark:text-blue-100">Total Invoices</CardTitle>
                  <FileText className="h-5 w-5 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                    {invoiceStats.total_invoices}
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Total Amount: {formatCurrency(invoiceStats.total_amount)}
                  </p>
                  <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                    <Button variant="ghost" size="sm" className="h-6 text-xs -ml-2" asChild>
                      <Link to="/invoices">
                        View all <ArrowUpRight className="h-3 w-3 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-yellow-900 dark:text-yellow-100">Pending Invoices</CardTitle>
                  <Clock className="h-5 w-5 text-yellow-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-yellow-700 dark:text-yellow-300">
                    {invoiceStats.pending_invoices}
                  </div>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    Amount Due: {formatCurrency(invoiceStats.pending_amount)}
                  </p>
                  <div className="mt-2 pt-2 border-t border-yellow-200 dark:border-yellow-800">
                    <Link to="/invoices?status=pending" className="text-xs text-yellow-700 dark:text-yellow-300 hover:underline">
                      View pending
                    </Link>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-red-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-red-900 dark:text-red-100">Overdue Invoices</CardTitle>
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-red-700 dark:text-red-300">
                    {invoiceStats.overdue_invoices}
                  </div>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Requires attention
                  </p>
                  <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-800">
                    {invoiceStats.overdue_invoices > 0 && (
                      <Link to="/invoices?status=overdue" className="text-xs text-red-700 dark:text-red-300 hover:underline">
                        View overdue
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-green-900 dark:text-green-100">Collected</CardTitle>
                  <DollarSign className="h-5 w-5 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-700 dark:text-green-300">
                    {invoiceStats.completed_invoices}
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    Amount: {formatCurrency(invoiceStats.received_amount)}
                  </p>
                  <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800">
                    <div className="text-xs text-green-600 dark:text-green-400">
                      {invoiceStats.total_amount > 0
                        ? `${Math.round((invoiceStats.received_amount / invoiceStats.total_amount) * 100)}% collected`
                        : 'No invoices yet'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Overdue Invoices Alert */}
      {overdueInvoices && overdueInvoices.length > 0 && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/10">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-800 dark:text-red-300">
              <AlertCircle className="h-5 w-5" />
              <span>Overdue Invoices</span>
            </CardTitle>
            <CardDescription className="text-red-600 dark:text-red-400">
              {overdueInvoices.length} invoice(s) are overdue and require immediate attention.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              {overdueInvoices.slice(0, 3).map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-red-200 dark:border-red-800">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {invoice.invoice_number} - {invoice.customer_name || 'Walk-in'}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Due: {new Date(invoice.due_date).toLocaleDateString('en-IN')}
                      {invoice.days_overdue && ` (${invoice.days_overdue} days overdue)`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-red-700 dark:text-red-300">
                      {formatCurrency(invoice.total_amount)}
                    </div>
                    <Link
                      to={`/invoices/${invoice.id}`}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      View →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm text-red-700 dark:text-red-300">
                Total overdue amount: {formatCurrency(overdueInvoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0))}
              </div>
              <Button variant="destructive" size="sm" asChild>
                <Link to="/invoices?status=overdue">View All Overdue</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low Stock Alert */}
      {dashboardData?.low_stock_products && dashboardData.low_stock_products.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              <span>Low Stock Alert</span>
            </CardTitle>
            <CardDescription className="text-red-600">
              {dashboardData?.low_stock_products?.length || 0} product(s) are running low on stock and need restocking.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-sm text-red-700">
                Critical items: {dashboardData?.low_stock_products?.filter(p => parseInt(p.stock_quantity) === 0).length || 0} out of stock
              </div>
              <Button variant="destructive" size="sm" asChild>
                <Link to="/stock">Manage Stock</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions - Compact */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Button className="h-16 flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700" asChild>
          <Link to="/pos">
            <ShoppingCart className="h-5 w-5" />
            <span className="text-sm font-medium">New Sale</span>
          </Link>
        </Button>
        <Button variant="outline" className="h-16 flex items-center justify-center gap-2" asChild>
          <Link to="/products/new">
            <Plus className="h-5 w-5" />
            <span className="text-sm font-medium">Add Product</span>
          </Link>
        </Button>
        <Button variant="outline" className="h-16 flex items-center justify-center gap-2" asChild>
          <Link to="/customers">
            <Users className="h-5 w-5" />
            <span className="text-sm font-medium">Customers</span>
          </Link>
        </Button>
        <Button variant="outline" className="h-16 flex items-center justify-center gap-2" asChild>
          <Link to="/stock">
            <Package className="h-5 w-5" />
            <span className="text-sm font-medium">Stock</span>
          </Link>
        </Button>
        <Button variant="outline" className="h-16 flex items-center justify-center gap-2" asChild>
          <Link to="/sales">
            <Receipt className="h-5 w-5" />
            <span className="text-sm font-medium">Sales</span>
          </Link>
        </Button>
        <Button variant="outline" className="h-16 flex items-center justify-center gap-2" asChild>
          <Link to="/products">
            <FileText className="h-5 w-5" />
            <span className="text-sm font-medium">Products</span>
          </Link>
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Selling Products */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Award className="h-4 w-4 text-yellow-600" />
              Top Products
            </CardTitle>
            <CardDescription className="text-xs">
              Best performers today
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData?.top_products && dashboardData.top_products.length > 0 ? (
              <div className="space-y-2">
                {dashboardData.top_products.slice(0, 5).map((product, index) => (
                  <div key={product.id} className="flex items-center justify-between p-2 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/10 dark:to-orange-900/10 rounded-lg border border-yellow-100 dark:border-yellow-800">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs ${
                        index === 0 ? 'bg-yellow-400 text-yellow-900' :
                        index === 1 ? 'bg-gray-300 text-gray-700' :
                        index === 2 ? 'bg-orange-300 text-orange-900' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{product.name}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {product.total_quantity} units
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm text-green-600 dark:text-green-400">
                        {formatCurrency(parseFloat(product.total_revenue))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Award className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">No sales data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Method Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4 text-green-600" />
              Payment Methods
            </CardTitle>
            <CardDescription className="text-xs">
              Today's payment breakdown
            </CardDescription>
          </CardHeader>
          <CardContent>
            {getPaymentBreakdown().length > 0 ? (
              <div className="space-y-3">
                {getPaymentBreakdown().map((payment) => (
                  <div key={payment.method} className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Badge
                          variant={payment.method === 'CASH' ? 'default' : 'outline'}
                          className={`flex-shrink-0 ${
                            payment.method === 'CASH' ? 'bg-green-500' :
                            payment.method === 'CARD' ? 'bg-blue-500 text-white' :
                            payment.method === 'UPI' ? 'bg-purple-500 text-white' :
                            'bg-gray-500 text-white'
                          }`}
                        >
                          {payment.method}
                        </Badge>
                        <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {payment.count} {payment.count === 1 ? 'sale' : 'sales'}
                        </span>
                      </div>
                      <p className="font-bold text-sm text-gray-900 dark:text-gray-100 flex-shrink-0">
                        {formatCurrency(payment.amount)}
                      </p>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full ${
                          payment.method === 'CASH' ? 'bg-green-500' :
                          payment.method === 'CARD' ? 'bg-blue-500' :
                          payment.method === 'UPI' ? 'bg-purple-500' :
                          'bg-gray-500'
                        }`}
                        style={{
                          width: `${Math.min(100, (payment.amount / (dashboardData?.stats?.sales?.total_revenue || 1)) * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                ))}
                <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total</span>
                    <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(getPaymentBreakdown().reduce((sum, p) => sum + p.amount, 0))}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">No payment data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Sales */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4 text-blue-600" />
                  Recent Sales
                </CardTitle>
                <CardDescription className="text-xs">
                  Latest transactions
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="h-8" asChild>
                <Link to="/sales">
                  <Eye className="h-3 w-3" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {dashboardData?.recent_sales && dashboardData.recent_sales.length > 0 ? (
              <div className="space-y-2">
                {dashboardData.recent_sales.slice(0, 5).map((sale) => (
                  <div key={sale.id} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                          {sale.customer_name || 'Walk-in'}
                        </p>
                        {sale.payment_method && (
                          <Badge
                            size="sm"
                            className={`text-xs ${
                              sale.payment_method === 'cash' ? 'bg-green-500' :
                              sale.payment_method === 'card' ? 'bg-blue-500' :
                              sale.payment_method === 'upi' ? 'bg-purple-500' :
                              'bg-gray-500'
                            } text-white`}
                          >
                            {sale.payment_method.toUpperCase()}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {new Date(sale.created_at).toLocaleString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: '2-digit',
                          month: 'short'
                        })}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm text-blue-600 dark:text-blue-400">
                        {formatCurrency(parseFloat(sale.total_amount))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">No recent sales</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Business Summary Banner */}
      {dashboardData && (
        <Card className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 border-indigo-200 dark:border-indigo-800">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center">
                <div className="text-4xl mr-4">🏪</div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                    {user?.business_name || 'Your Business'}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Period: {dashboardData.period ? new Date(dashboardData.period.from).toLocaleDateString('en-IN') : ''} - {dashboardData.period ? new Date(dashboardData.period.to).toLocaleDateString('en-IN') : ''}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <p className="text-gray-600 dark:text-gray-400">Products</p>
                  <p className="font-bold text-lg text-gray-900 dark:text-gray-100">
                    {dashboardData?.stats?.products?.total_products || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-gray-600 dark:text-gray-400">Customers</p>
                  <p className="font-bold text-lg text-gray-900 dark:text-gray-100">
                    {dashboardData?.stats?.customers?.total_customers || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-gray-600 dark:text-gray-400">Sales</p>
                  <p className="font-bold text-lg text-green-600 dark:text-green-400">
                    {dashboardData?.stats?.sales?.total_sales || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-gray-600 dark:text-gray-400">Revenue</p>
                  <p className="font-bold text-lg text-green-600 dark:text-green-400">
                    {formatCurrency(dashboardData?.stats?.sales?.total_revenue || 0)}
                  </p>
                </div>
              </div>
            </div>
            {(dashboardData?.low_stock_products && dashboardData.low_stock_products.length > 0) && (
              <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-800">
                <p className="text-sm text-orange-600 dark:text-orange-400 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {dashboardData.low_stock_products.length} product(s) need restocking
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}